import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useEmpresaAtiva } from "./useEmpresaAtiva";
import { setVerComoId } from "../lib/api";
import { useAuth } from "./useAuth";

/**
 * "Ver como" — lente global de visão.
 *
 * O admin escolhe um usuário (cliente do portal OU usuário interno) e o painel
 * inteiro passa a renderizar o que AQUELA pessoa veria: o menu lateral some o
 * que ela não acessa (papel + perfil/RBAC), e os dados ficam no escopo dela
 * (todo GET carrega ?como=<id> → o backend escopa via escopoUnidades).
 *
 * É PRÉ-VISUALIZAÇÃO, não login: a sessão continua sendo a do admin; escritas
 * nunca herdam a lente. Estado por aba (sessionStorage) e limpo ao trocar de
 * empresa. Trocar a lente limpa o cache do react-query pra tudo refazer no
 * novo escopo.
 */
export interface VerComoAlvo {
  id: string;
  nome: string;
  email?: string | null;
  role: string; // papel efetivo na empresa ativa (admin | encarregado | cliente)
  perfil: string; // perfil RBAC na empresa ativa
  clienteId?: string | null;
  clienteNome?: string | null;
  empresaSlug: string; // empresa em que a lente foi ligada (valida ao restaurar)
}

interface VerComoCtx {
  alvo: VerComoAlvo | null;
  setAlvo: (a: VerComoAlvo) => void;
  limpar: () => void;
}

const Ctx = createContext<VerComoCtx>({ alvo: null, setAlvo: () => {}, limpar: () => {} });
const KEY = "m5_ver_como";

export function VerComoProvider({ children }: { children: React.ReactNode }) {
  const { ativa } = useEmpresaAtiva();
  const qc = useQueryClient();
  const [alvo, setAlvoState] = useState<VerComoAlvo | null>(() => {
    try {
      const raw = sessionStorage.getItem(KEY);
      return raw ? (JSON.parse(raw) as VerComoAlvo) : null;
    } catch {
      return null;
    }
  });

  // Sincroniza o holder do api client + persiste por aba.
  useEffect(() => {
    setVerComoId(alvo?.id ?? null);
    try {
      if (alvo) sessionStorage.setItem(KEY, JSON.stringify(alvo));
      else sessionStorage.removeItem(KEY);
    } catch {
      /* ignore */
    }
  }, [alvo]);

  // 🔍 A7/N4 (14/10): sob a lente, os botões que GRAVAM (`data-grava`) ficam desabilitados — visual (CSS em index.css,
  // `html[data-lente="so-leitura"]`) e de fato (o clique e o Enter são barrados aqui, na captura). O apiFetch e o servidor
  // recusam a escrita também: este é o primeiro dos três cadeados, o que a pessoa vê.
  useEffect(() => marcarLenteSoLeitura(!!alvo), [alvo]);

  // A lente é por empresa: trocou de empresa, a visão volta pro Operador.
  useEffect(() => {
    if (alvo && ativa?.slug && alvo.empresaSlug !== ativa.slug) {
      setAlvoState(null);
      setVerComoId(null);
      qc.clear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ativa?.slug]);

  const setAlvo = useCallback(
    (a: VerComoAlvo) => {
      setVerComoId(a.id); // antes do clear, pra o refetch já sair com a lente
      setAlvoState(a);
      qc.clear();
    },
    [qc]
  );
  const limpar = useCallback(() => {
    setVerComoId(null);
    setAlvoState(null);
    qc.clear();
  }, [qc]);

  return <Ctx.Provider value={{ alvo, setAlvo, limpar }}>{children}</Ctx.Provider>;
}

/** Liga/desliga o modo só leitura da lente no documento. Devolve a função que desfaz (efeito do React). Exportada p/ teste. */
export function marcarLenteSoLeitura(ligada: boolean): () => void {
  if (typeof document === "undefined") return () => {};
  const raiz = document.documentElement;
  if (!ligada) { delete raiz.dataset.lente; return () => {}; }
  raiz.dataset.lente = "so-leitura";
  const barrar = (ev: Event) => {
    const alvo = (ev.target as Element | null)?.closest?.("[data-grava]");
    if (!alvo) return;
    ev.preventDefault();
    ev.stopPropagation();
  };
  document.addEventListener("click", barrar, true);
  document.addEventListener("submit", barrar, true);
  return () => { document.removeEventListener("click", barrar, true); document.removeEventListener("submit", barrar, true); delete raiz.dataset.lente; };
}

/** A lente está ligada (global ou `?como=`)? Para `disabled={soLeitura}` nos botões que gravam. */
export function useLenteSoLeitura(como?: string | null): { soLeitura: boolean; motivo: string } {
  const { alvo } = useVerComo();
  return { soLeitura: !!alvo || !!como, motivo: "Prévia \"ver como\": só leitura. Saia da prévia para agir." };
}

export function useVerComo() {
  return useContext(Ctx);
}

/**
 * 🔒 S1/A7 (09/10) — O PAPEL QUE A TELA DEVE OBEDECER. Sob a lente "Ver como" (global, `alvo`) ou no
 * deep-link `?como=`, o operador está PRÉ-VISUALIZANDO a tela do cliente: nenhum botão de operador pode
 * aparecer nem agir. Telas que liam `user.role` cru (auditoria de boletos, renovações da carteira)
 * mostravam o gate "segurar/liberar", o re-auditar e o relatório do período ao admin sob a lente — e os
 * botões gravavam como admin. Esta é a fonte única: `operador` só é verdade fora de qualquer lente.
 */
export function usePapelEfetivo(como?: string | null): { papel: string | null; operador: boolean; preview: boolean } {
  const { user } = useAuth();
  const { alvo } = useVerComo();
  const preview = !!alvo || !!como;
  const papel = alvo?.role ?? user?.role ?? null;
  const operador = !preview && (user?.role === "admin" || user?.role === "encarregado");
  return { papel, operador, preview };
}
